import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { toast } from 'sonner';

export interface PricingPlan {
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  popular: boolean;
  discount: string | null;
  annualTotal?: number | null;
  priceId?: {
    monthly: string;
    annual: string;
  };
}

interface PricingCardProps {
  plan: PricingPlan;
  isAnnual: boolean;
}

/**
 * PricingCard component displays an individual pricing plan card
 * with details such as price, features, and call to action
 */
const PricingCard: React.FC<PricingCardProps> = ({ plan, isAnnual }) => {
  const { startCheckout } = useStripeCheckout();
  const [loading, setLoading] = useState(false);
  
  const handleSubscribe = async () => {
    try {
      setLoading(true);
      
      // Get the appropriate price ID based on plan and interval
      let priceId;
      
      if (plan.priceId) {
        // If plan has specific price IDs defined, use those
        priceId = isAnnual ? plan.priceId.annual : plan.priceId.monthly;
        console.log(`Using direct price ID for ${plan.name}: ${priceId}`);
      } else {
        // Fallback to using the plan name as a key
        const planName = plan.name.toLowerCase().replace(/\s+/g, '-');
        priceId = planName;
        console.log(`Using fallback plan name for price ID: ${priceId}`);
      }
      
      if (!priceId) {
        throw new Error(`No price ID found for ${plan.name}`);
      }
      
      // Start the checkout process
      const interval = isAnnual ? 'annual' : 'monthly';
      console.log(`Starting checkout for ${plan.name} (${interval}) with price ID: ${priceId}`);
      await startCheckout(priceId, interval);
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast.error('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card 
      className={`flex flex-col transform transition-all duration-500 hover:-translate-y-3 hover:shadow-xl rounded-2xl ${
        plan.popular 
          ? 'border-blue-500/50 relative bg-gradient-to-b from-gray-800/70 to-gray-900/80 shadow-blue-500/20 shadow-md' 
          : 'border-gray-700/30 bg-gray-800/20 hover:border-gray-600/50'
      }`}
    >
      {plan.name.toLowerCase() === "free" && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
          Limited Time Offer
        </div>
      )}
      
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md shadow-blue-500/20">
          Most Popular
        </div>
      )}
      
      <CardHeader>
        <CardTitle className="text-lg text-center text-gray-100">{plan.name}</CardTitle>
      </CardHeader>
      
      <CardContent className="flex-grow">
        <div className="text-center mb-6">
          <div className="flex items-end justify-center">
            {plan.name.toLowerCase() === "free" ? (
              <div className="text-3xl font-bold text-white">$0</div>
            ) : (
              <>
                <span className="text-xl text-gray-400 mr-1">$</span>
                <div className="text-3xl font-bold text-white">{plan.price}</div>
              </>
            )}
          </div>
          <div className="text-gray-400 text-sm">{plan.period}</div>
          {plan.discount && (
            <div className="mt-2 text-green-400 text-xs font-medium bg-green-900/20 rounded-full py-1 px-2 inline-block">
              {plan.discount}
            </div>
          )}
          {isAnnual && plan.annualTotal && (
            <div className="mt-1 text-gray-300 text-xs">
              Total: ${plan.annualTotal}/year
            </div>
          )}
        </div>
        
        <p className="text-gray-300 text-sm text-center mb-6">
          {plan.description}
        </p>
        
        <ul className="space-y-3">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start">
              <div className="rounded-full p-1 bg-blue-900/40 text-blue-300 mr-2 shrink-0">
                <Check className="h-4 w-4" />
              </div>
              <span className={`text-sm ${feature.includes("report") && feature.includes("$") ? "text-blue-300 font-medium" : "text-gray-300"}`}>
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter className="pt-4">
        <Button 
          className={`w-full transition-all duration-300 transform hover:translate-y-[-2px] rounded-xl ${
            plan.popular 
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 shadow-md shadow-blue-600/20' 
              : 'bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 shadow-md shadow-gray-900/30'
          }`}
          onClick={handleSubscribe}
          disabled={loading}
        >
          {loading ? 'Processing...' : plan.cta}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PricingCard;
